import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Link2, ExternalLink, ShieldCheck, ArrowUpRight, Share2 } from "lucide-react";
import { formatPrice, formatNumber, formatPct } from "../lib/format";
import ShareModal from "../components/terminal/ShareModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_COLOR = {
    ORDER_PLACED: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    ORDER_MATCHED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    MARGIN_ADDED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    EARLY_EXIT: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    LIQUIDATION: "text-rose-400 border-rose-500/30 bg-rose-500/10",
    SETTLEMENT: "text-violet-400 border-violet-500/30 bg-violet-500/10",
};

export default function Proof() {
    const { swapId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [showShare, setShowShare] = useState(false);

    useEffect(() => {
        axios.get(`${API}/public/swap/${swapId}/proof`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.status === 404 ? "Settled swap not found" : "Could not load proof"))
            .finally(() => setLoading(false));
    }, [swapId]);

    if (loading) {
        return (
            <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] grid place-items-center">
                <div className="text-[11px] font-mono text-zinc-600">loading proof…</div>
            </div>
        );
    }

    if (err || !data) {
        return (
            <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] grid place-items-center px-6">
                <div className="text-center">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-rose-400">404</div>
                    <h1 className="mt-2 text-2xl font-medium tracking-tighter">{err || "Proof unavailable"}</h1>
                    <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto">
                        Proof pages are only generated for settled BUBE swaps. If the swap is still
                        active, come back after expiry.
                    </p>
                    <Link to="/" className="inline-block mt-6 text-[11px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300">
                        ← Back to Verslan
                    </Link>
                </div>
            </div>
        );
    }

    const { swap, events } = data;
    const priceMove = swap.entry_price ? ((swap.exit_price - swap.entry_price) / swap.entry_price) * 100 : 0;
    const onchainCount = events.filter((e) => !!e.tx_hash).length;
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareText = `${swap.coin} BUBE swap settled ${swap.outcome} (${swap.exit_type}) on Verslan — ${onchainCount} on-chain events verifiable on Hyperliquid testnet.`;

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)]">
            <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-14">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">
                            BUBE swap · proof of execution
                        </div>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-medium tracking-tighter">
                            {swap.coin} settled <span className={swap.outcome === "BULLISH" ? "text-emerald-400" : swap.outcome === "BEARISH" ? "text-rose-400" : "text-zinc-300"}>{swap.outcome}</span>
                        </h1>
                        <div className="mt-1.5 text-[11px] font-mono text-zinc-500">
                            ID <span className="text-zinc-300">{swap.id}</span>
                            <span className="mx-2 text-zinc-700">·</span>
                            {swap.duration_key} · {swap.exit_type}
                        </div>
                    </div>
                    <button
                        data-testid="proof-share-btn"
                        onClick={() => setShowShare(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400 hover:bg-cyan-300 text-black text-xs uppercase tracking-widest font-semibold rounded-sm"
                    >
                        <Share2 className="w-3.5 h-3.5" /> Share
                    </button>
                </div>

                <div className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400/90 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 rounded-sm mb-6">
                    <ShieldCheck className="w-3 h-3" />
                    {onchainCount} event{onchainCount === 1 ? "" : "s"} verified on Hyperliquid testnet · zero-cost attestation
                </div>

                {/* Summary grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                    <Stat label="Notional" value={`$${formatNumber(swap.notional)}`} />
                    <Stat label="Entry" value={formatPrice(swap.entry_price)} />
                    <Stat label="Exit" value={formatPrice(swap.exit_price)} accent={priceMove >= 0 ? "text-emerald-400" : "text-rose-400"} />
                    <Stat label="Price move" value={formatPct(priceMove, 3)} accent={priceMove >= 0 ? "text-emerald-400" : "text-rose-400"} />
                    <Stat label="Winner PnL" value={`$${formatNumber(swap.pnl_abs)}`} accent="text-emerald-400" />
                    <Stat label="Outcome" value={swap.outcome} accent={swap.outcome === "BULLISH" ? "text-emerald-400" : swap.outcome === "BEARISH" ? "text-rose-400" : "text-zinc-300"} />
                    <Stat label="Settled at" value={swap.settled_at ? new Date(swap.settled_at).toLocaleString() : "—"} small />
                    <Stat label="Duration" value={swap.duration_key} />
                </div>

                {/* Events */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#1F1F24] flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                            On-chain events ({events.length})
                        </div>
                        <Link to="/onchain" className="inline-flex items-center gap-1 text-[10px] font-mono text-cyan-400 hover:text-cyan-300">
                            Public tape <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                    {events.length === 0 ? (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">
                            No on-chain events recorded for this swap.
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#1F1F24]">
                            {events.map((ev) => {
                                const color = EVENT_COLOR[ev.event_type] || "text-zinc-400 border-zinc-700 bg-zinc-700/10";
                                const clickable = !!ev.explorer_url && !!ev.tx_hash;
                                const Tag = clickable ? "a" : "div";
                                const props = clickable
                                    ? { href: ev.explorer_url, target: "_blank", rel: "noopener noreferrer" }
                                    : {};
                                return (
                                    <li key={ev.id}>
                                        <Tag
                                            {...props}
                                            data-testid={`proof-event-${ev.id}`}
                                            className={`flex items-center gap-3 px-4 sm:px-5 py-3 ${clickable ? "hover:bg-[#0F0F12]" : "opacity-70"}`}
                                        >
                                            <span className={`shrink-0 text-[9.5px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm border ${color}`}>
                                                {ev.event_type.replace("_", " ")}
                                            </span>
                                            <span className="font-mono text-[10px] text-zinc-500 shrink-0 hidden sm:inline">
                                                {new Date(ev.attempted_at).toLocaleString()}
                                            </span>
                                            <div className="flex-1" />
                                            {ev.tx_hash ? (
                                                <span className="inline-flex items-center gap-1 font-mono text-[10px] text-cyan-400/90 shrink-0">
                                                    <Link2 className="w-3 h-3" />
                                                    <span className="truncate max-w-[120px] sm:max-w-[220px]">{ev.tx_hash}</span>
                                                    <ExternalLink className="w-3 h-3" />
                                                </span>
                                            ) : (
                                                <span className="font-mono text-[10px] text-amber-400/80 shrink-0">pending</span>
                                            )}
                                        </Tag>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="mt-8 text-center text-[10px] font-mono text-zinc-600">
                    Powered by <Link to="/" className="text-cyan-400 hover:text-cyan-300">Verslan BUBE Testnet</Link> · bounded-exposure derivative swaps on Hyperliquid.
                </div>
            </div>

            {showShare && (
                <ShareModal
                    title={`${swap.coin} BUBE swap · ${swap.outcome}`}
                    text={shareText}
                    url={shareUrl}
                    onClose={() => setShowShare(false)}
                />
            )}
        </div>
    );
}

const Stat = ({ label, value, accent = "text-zinc-100", small = false }) => (
    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-3">
        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className={`mt-1 font-mono ${small ? "text-[11px]" : "text-base"} ${accent}`}>{value}</div>
    </div>
);
